import React, { useEffect, useRef, useContext } from "react";
import "./map.css";
import "maplibre-gl/dist/maplibre-gl.css";
import * as maptilersdk from "@maptiler/sdk";
import "@maptiler/sdk/dist/maptiler-sdk.css";

import customThree from "src/layers/customThreeSmooth";
import customPolyline from "src/layers/3DPolyline";
import { Panel } from "src/components/dropdown/dropdown";
import TimeSelection from "../timeselection/timeselection";
import { DataContext } from "src/contexts/DataContext";
import { TimingContext } from "src/contexts/TimingContext";
import { MapContext } from "src/contexts/MapContext";

const center = [
  -121.99115753173828, 36.34485825859083,
] as maptilersdk.LngLatLike;
export const limit = 10000000;
export const exaggeration = 2.5;
const ADD_OVERLAY = true;
const TOPOBATH_SRC = "CUSTOM";

export default function Map() {
  if (process.env.REACT_APP_API_KEY == null) {
    throw new Error("You have to configure env REACT_APP_API_KEY, see README");
  }
  const map = useRef<maptilersdk.Map>();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const seal = "Seal._2";

  const { data, cmap, selectedVar } = useContext(DataContext);
  const { playing, speed } = useContext(TimingContext);

  useEffect(() => {
    maptilersdk.config.apiKey = process.env.REACT_APP_API_KEY;
    map.current = new maptilersdk.Map({
      container: "map", // container id
      // style: `https://api.maptiler.com/maps/ocean/style.json?key=${process.env.REACT_APP_API_KEY}`,
      style: "ocean",
      center: center, // starting position [lng, lat]
      zoom: 11, // starting zoom
      pitch: 65.5,
      maxPitch: 80,
      maxZoom: 15,
      bearing: 44,
      preserveDrawingBuffer: true,
      forceNoAttributionControl: true,
    });

    map.current.on("load", function () {
      if (TOPOBATH_SRC === "CUSTOM") {
        map.current.addSource("custom-dem", {
          type: "raster-dem",
          tiles: [
            "https://cogserver-staging-myzvqet7ua-uw.a.run.app/get_rgb_tile/{z}/{x}/{y}.png?dataset=GlobalTopoBath.tif",
          ],
          tileSize: 512,
          minzoom: 0,
          maxzoom: 7,
        });
        map.current.setTerrain({
          source: "custom-dem",
          exaggeration: exaggeration,
        });
      } else {
        map.current.addSource("bathymetry", {
          type: "raster-dem",
          url: `https://api.maptiler.com/tiles/ocean-rgb/tiles.json?key=${process.env.REACT_APP_API_KEY}`,
        });
        map.current.setTerrain({
          source: "bathymetry",
          exaggeration: exaggeration,
        });
      }
      if (ADD_OVERLAY) {
        map.current.addSource("wms-test-source", {
          type: "raster",
          tiles: [
            "https://gis.ngdc.noaa.gov/arcgis/services/DEM_mosaics/DEM_global_mosaic_hillshade/ImageServer/WMSServer?bbox={bbox-epsg-3857}&format=image/png&service=WMS&version=1.1.1&request=GetMap&srs=EPSG:3857&transparent=true&width=512&height=512&layers=DEM_global_mosaic_hillshade:ColorHillshade",
          ],
          tileSize: 256,
          minzoom: 0,
          maxzoom: 11,
        });
        map.current.addLayer({
          id: "wms-test-layer",
          type: "raster",
          source: "wms-test-source",
          paint: {},
        });
      }
    });

    return () => {
      map.current?.remove();
    };
  }, []);

  useEffect(() => {
    if (!map.current) return;
    if (data.length === 0) return;
    if (!cmap) return;
    try {
      map.current.removeLayer("3d-polyline");
      map.current.removeLayer("3d-model");
      map.current.addLayer(
        customThree(data, seal, map.current, { playing, speed }) as maptilersdk.CustomLayerInterface
      );
      map.current.addLayer(
        customPolyline(data, map.current, cmap, selectedVar) as maptilersdk.CustomLayerInterface
      );
    } catch (e) {
      console.error(e);
    }
    
  }, [data, cmap, playing, speed, selectedVar]);

  return (
    <MapContext.Provider value={{ map: map.current }}>
      <div ref={mapContainerRef} className="map fullscreen" id="map">
        <Panel />
        <TimeSelection />
      </div>
    </MapContext.Provider>
  );
}
